import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Building2, AlertCircle, AlertTriangle, CheckCircle, UserPlus, LogIn, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useOrgStore } from '../store/orgStore';
import { logoutUser } from '../services/authService';
import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';
import { INVITATION_STATUS_LABELS } from '../constants/invitationStatusLabels';

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
};

interface InvitationInfo {
  id: string;
  email: string;
  role: string;
  status: 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';
  organizationName: string;
  organizationId: string;
  inviterName: string;
  expiresAt: string;
  userExists: boolean; // CHG-141 REQ-IF-005
}

export default function OrgInvitationAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const logoutLocal = useAuthStore((s) => s.logout);
  const { setActiveOrgId, fetchOrganizations, organizations } = useOrgStore();

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await api.get<ApiResponse<InvitationInfo>>(`/org-invitations/${token}`);
        setInvitation(res.data.data!);
        setLoadingState('loaded');
      } catch {
        setErrorMsg('유효하지 않은 초대 링크입니다.');
        setLoadingState('error');
      }
    })();
  }, [token]);

  // ============================== 액션 ==============================

  const handleAccept = async () => {
    if (!token || accepting) return;
    setAccepting(true);
    try {
      const res = await api.post<ApiResponse<{ organizationId: string; organizationName: string }>>(
        `/org-invitations/${token}/accept`,
      );
      const data = res.data.data!;
      setAccepted(true);
      setActiveOrgId(data.organizationId);
      await fetchOrganizations();
      toast.success(res.data.message || '조직에 합류했습니다!');
      setTimeout(() => navigate('/', { replace: true }), 1000);
    } catch (err) {
      const errData = (err as { response?: { data?: { code?: string; error?: string; message?: string } } })?.response?.data;
      // CHG-141: INVITATION_EMAIL_MISMATCH 응답이면 화면 분기로 안내
      if (errData?.code === 'INVITATION_EMAIL_MISMATCH') {
        toast.error('현재 로그인 계정으로는 합류할 수 없습니다.');
      } else {
        toast.error(errData?.message || errData?.error || '초대 수락에 실패했습니다.');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleLogoutAndRetry = async () => {
    if (!invitation || loggingOut) return;
    setLoggingOut(true);
    try {
      if (refreshToken) await logoutUser(refreshToken);
    } catch {
      // 서버 로그아웃 실패해도 로컬은 무조건 정리
    }
    logoutLocal();
    const returnUrl = `/org-invitations/${token}/accept`;
    navigate(
      `/login?expectEmail=${encodeURIComponent(invitation.email)}&returnUrl=${encodeURIComponent(returnUrl)}`,
      { replace: true },
    );
  };

  // ============================== 로딩 / 에러 ==============================

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="size-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (loadingState === 'error' || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="size-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">초대를 처리할 수 없습니다</h2>
          <p className="text-gray-500 mb-6">{errorMsg}</p>
          <Link to="/" className="text-primary-600 font-medium hover:text-primary-700">
            홈으로 이동
          </Link>
        </div>
      </div>
    );
  }

  // ============================== 분기: 비-PENDING 상태 (EDGE-002) ==============================

  if (invitation.status !== 'PENDING') {
    const label = INVITATION_STATUS_LABELS[invitation.status];
    const isMemberOfOrg = !!organizations.find((o) => o.id === invitation.organizationId);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <AlertCircle className="size-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{label.title}</h2>
          <p className="text-gray-500 mb-6">{label.message}</p>
          {label.cta === 'go-org' && isMemberOfOrg ? (
            <button
              onClick={() => {
                setActiveOrgId(invitation.organizationId);
                navigate('/');
              }}
              className="text-primary-600 font-medium hover:text-primary-700"
            >
              조직으로 이동
            </button>
          ) : (
            <Link to="/" className="text-primary-600 font-medium hover:text-primary-700">
              홈으로 이동
            </Link>
          )}
        </div>
      </div>
    );
  }

  // ============================== 분기: 인증됨 + 이메일 불일치 (REQ-IF-014) ==============================

  if (isAuthenticated && currentUser && currentUser.email.trim().toLowerCase() !== invitation.email.trim().toLowerCase()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="size-7 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">다른 계정으로 로그인되어 있습니다</h2>
            <p className="text-gray-500 mb-2 text-sm">
              현재 <strong className="text-gray-700">{currentUser.email}</strong>으로 로그인 중입니다.
            </p>
            <p className="text-gray-500 mb-1 text-sm">
              이 초대는 <strong className="text-gray-700">{invitation.email}</strong> 대상입니다.
            </p>
            <p className="text-gray-500 mt-3 text-sm">
              계속하려면 로그아웃 후 해당 계정으로 다시 로그인해주세요.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleLogoutAndRetry}
              disabled={loggingOut}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <LogOut className="size-4" />
              {loggingOut ? '로그아웃 중...' : '로그아웃 후 다시 시도'}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-white text-gray-600 py-2.5 px-4 rounded-lg text-sm font-medium
                border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================== 분기: 수락 완료 ==============================

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <CheckCircle className="size-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">합류 완료!</h2>
          <p className="text-gray-500">잠시 후 대시보드로 이동합니다...</p>
        </div>
      </div>
    );
  }

  // ============================== 분기: 인증됨 + 이메일 일치 → [수락] / [거절] ==============================

  if (isAuthenticated && currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary-600">MaioS</h1>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="size-7 text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">워크스페이스 초대</h2>
              <p className="text-gray-500">
                <strong className="text-gray-700">{invitation.inviterName}</strong>님이{' '}
                <strong className="text-gray-700">{invitation.organizationName}</strong>에 초대했습니다.
              </p>
              <span className="inline-block mt-2 text-sm bg-gray-100 text-gray-600 rounded px-2 py-1">
                역할: {ROLE_LABELS[invitation.role] ?? invitation.role}
              </span>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                  hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {accepting ? '합류 중...' : '초대 수락하기'}
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-white text-gray-600 py-2.5 px-4 rounded-lg text-sm font-medium
                  border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                나중에 하기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================== 분기: 미인증 + userExists=false → 회원가입 안내 ==============================

  if (!invitation.userExists) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary-600">MaioS</h1>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="size-7 text-primary-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">회원가입 후 자동으로 합류됩니다</h2>
              <p className="text-gray-500 text-sm">
                <strong className="text-gray-700">{invitation.email}</strong>로 회원가입을 진행하시면
              </p>
              <p className="text-gray-500 text-sm">
                이메일 인증 완료 후 <strong className="text-gray-700">{invitation.organizationName}</strong>에 자동으로 합류됩니다.
              </p>
            </div>
            <Link
              to={`/register?invitation=${token}&type=ORG`}
              className="block w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 transition-colors text-center"
            >
              회원가입 시작
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================== 분기: 미인증 + userExists=true → 로그인 안내 ==============================

  const returnUrl = `/org-invitations/${token}/accept`;
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600">MaioS</h1>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="size-7 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">로그인 후 합류 가능합니다</h2>
            <p className="text-gray-500 text-sm">
              <strong className="text-gray-700">{invitation.email}</strong>으로 로그인해주세요.
            </p>
          </div>
          <Link
            to={`/login?expectEmail=${encodeURIComponent(invitation.email)}&returnUrl=${encodeURIComponent(returnUrl)}`}
            className="block w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
              hover:bg-primary-700 transition-colors text-center"
          >
            로그인하기
          </Link>
        </div>
      </div>
    </div>
  );
}
